// Question definitions for the SCS "Clinical Laboratory Evaluations" wizard.
//
// Mirrors scsAnalysisOfAdverseEventsQuestions — the two pages ask the same
// shaped questions (a table, the categories to report, then one or more
// comparison groupings), so they share attribute names wherever the answer is
// the same answer: `number` is the stable badge shown in the CSR-style index
// badge (page order, hardcoded — it does not renumber as conditional sections
// toggle), `text` is the fallback wording and `tooltip` holds the ticket
// INFORMATION/helper text (the endpoint returns wording only, no tooltip map).
//
// At runtime the wording is overridden by
// GET /clinical_summaries/safety_summary/questions, keyed by the same attribute
// the answer is stored under. `textKeys` lists the further attributes to read
// that wording from when the endpoint carries the question under another name —
// the comparison questions (Q4–Q6) are the Description of Studies questions
// repeated per grouping, so they reuse that page's wording and only their badge
// number changes.

export const SCS_CLE_QUESTIONS = {
  qLabTable: {
    number: 1,
    text: "What Clinical laboratory evaluation table do you want to report on?",
  },
  qLabEvalTypes: {
    number: 2,
    required: true,
    text: "Which evaluations do you want to report?",
  },
  qLabAdditionalGroupingName: {
    number: 3,
    text: "Which additional groupings do you want to compare? Name the grouping and then select the studies in this grouping.",
  },
  qLabComparisonType: {
    number: 4,
    // The endpoint has no wording of its own for this question yet; it is the
    // same question as Q7 of the Description of Studies page.
    textKeys: ["qComparisonType"],
    text: "How is the grouping compared?",
  },
  // Q5 / Q6 below are the Description of Studies branch questions (its Q8 / Q9)
  // shown for the comparison type picked in Q4. The Non-Comparative names and
  // the Comparative treatment groups are two different questions on screen but
  // are saved under the same qLabTreatmentGroups attribute (a grouping only
  // ever has one comparison type), so the Non-Comparative one keeps its own
  // key here.
  qLabNonComparativeGroupNames: {
    number: 5,
    textKeys: ["qNonComparativeGroupNames"],
    text: "What are the actual names used to describe the treatment/analysis groups in the column headers?",
  },
  qLabTreatmentGroups: {
    number: 5,
    textKeys: ["qTreatmentGroupNames"],
    text: "What are the actual names used to describe the treatment/analysis groups in the column headers? Please define the role for each column name.",
  },
  qLabComparisonPairs: {
    number: 6,
    textKeys: ["qComparisonPairs"],
    text: "Which dose/treatment groups should be compared?",
  },
  qLabDoseGroups: {
    number: 5,
    textKeys: ["qDoseGroups"],
    text: "Please list the dose group column headers from lowest to highest, starting with Placebo if present.",
  },
};

// Comparison type (Q4) -> the value it is saved as. The question is answered
// with the on-screen wording (the same button group as Q7 of the Description of
// Studies page) but persisted as the backend's grouping kind, which also decides
// which branch answers a grouping carries:
//   Non-Comparative -> single_arm    qLabTreatmentGroups (plain names)
//   Comparative     -> multi_arm     qLabTreatmentGroups ({ name, role })
//                                    + qLabComparisonPairs
//   Dose-Finding    -> dose_finding  qLabDoseGroups
export const SCS_CLE_COMPARISON_TYPE_VALUES = {
  "Non-Comparative": "single_arm",
  Comparative: "multi_arm",
  "Dose-Finding": "dose_finding",
};

// Saved value -> the on-screen wording, for re-populating Q4 from saved data.
export const SCS_CLE_COMPARISON_TYPE_LABELS = Object.fromEntries(
  Object.entries(SCS_CLE_COMPARISON_TYPE_VALUES).map(([label, value]) => [
    value,
    label,
  ])
);

// The clinical laboratory evaluation categories offered by Q2. `label` is both
// the multi-select option and the wording saved as the answer; `key` is the
// category's stable identifier.
//
// TODO: the questions endpoint carries no wording for these yet, so the labels
// are hardcoded here — swap them for the served wording once it is available.
export const SCS_LAB_EVAL_TYPE_OPTIONS = [
  { key: "clinical_chemistry", label: "Clinical chemistry" },
  { key: "haematology", label: "Haematology" },
  { key: "urinalysis", label: "Urinalysis" },
  { key: "vital_signs", label: "Vital signs" },
  { key: "ecg", label: "ECG" },
];
